/**
 * The FiXperience offline migration link carries `id` (activationKey) and
 * `validation` (validationKey) - together they authorise reading a real person's
 * home address - in the URL *hash* (moved there from the query string so they
 * never reach access logs, the gateway's request logger, or Referer headers;
 * fragments are never sent to the server at all). @sentry/vue's default
 * httpContext integration attaches `request.url = window.location.href` to every
 * event, and its fetch/xhr and navigation breadcrumbs carry URLs of their own -
 * `window.location.href` includes the hash, so any error during a migration-link
 * visit would otherwise ship both keys to Sentry regardless of where they live in
 * the URL. These helpers redact those two params in both the query string and the
 * hash - keeping the parameter names so the shape stays debuggable - wherever a
 * URL can be reached from Sentry's beforeSend/beforeBreadcrumb hooks.
 */

// No brackets: URLSearchParams percent-encodes them, which would make the
// redacted value round-trip differently ('%5Bredacted%5D') depending on whether
// it is read back out of a URLSearchParams or compared as a raw string.
export const REDACTED = 'redacted';

const SCRUBBED_PARAMS = ['id', 'validation'];

// Matches an `id=`/`validation=` token anchored at the start of the (already
// '#'-stripped) hash or right after an '&', and captures the separator so a
// valueless neighbour token (e.g. `registration-dialog`) survives byte-exact.
// Regex, not URLSearchParams: URLSearchParams.toString() adds a stray '=' to
// valueless tokens, which would break the `(?:^#|&)dialog(?:$|&)` matches other
// code relies on if this ever needed to round-trip a real hash.
const HASH_PARAM_PATTERN = /(^|&)(id|validation)=[^&]*/g;

const scrubQueryString = (beforeHash: string): string => {
  if (!beforeHash.includes('?')) return beforeHash;

  try {
    // A base is only needed to satisfy the URL constructor for relative URLs
    // (most breadcrumb/request URLs are relative); it never appears in the output.
    const parsed = new URL(beforeHash, 'http://sentry-url-scrubbing.invalid');
    const hadParam = SCRUBBED_PARAMS.some((param) => parsed.searchParams.has(param));
    if (!hadParam) return beforeHash;

    SCRUBBED_PARAMS.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, REDACTED);
      }
    });

    const prefix = beforeHash.slice(0, beforeHash.indexOf('?'));
    return `${prefix}${parsed.search}`;
  } catch {
    // Not a parseable URL - leave it alone rather than risk mangling it.
    return beforeHash;
  }
};

const scrubHash = (hash: string): string => {
  if (!hash) return hash;
  const withoutLeadingHash = hash.slice(1);
  const scrubbed = withoutLeadingHash.replace(
    HASH_PARAM_PATTERN,
    (_match, separator: string, param: string) => `${separator}${param}=${REDACTED}`,
  );
  return `#${scrubbed}`;
};

/**
 * Redacts SCRUBBED_PARAMS from a URL string's query string and hash, leaving
 * everything else - including an unrelated `id=` on a different host, or a key
 * that merely contains "id" such as `orderid=` - untouched. Returns the input
 * unchanged (including non-string/non-URL input) whenever there is nothing to
 * do, so callers can apply it unconditionally.
 */
export const scrubMigrationParamsFromUrl = (url: unknown): unknown => {
  if (typeof url !== 'string') return url;

  const hashIndex = url.indexOf('#');
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);

  if (!beforeHash.includes('?') && !hash) return url;

  return `${scrubQueryString(beforeHash)}${scrubHash(hash)}`;
};

// Typed loosely on purpose: these mirror Sentry's own breadcrumb/event shapes (which
// are themselves close to `any`), and a generic constrained to a narrow interface
// runs into spurious "no properties in common" errors against object literals that
// only carry the *other* fields (category, message, ...) - which is exactly what
// every real breadcrumb/event looks like.
type UnknownRecord = Record<string, unknown>;

/** Mutates and returns the breadcrumb: the url/to/from fields fetch, xhr and history (navigation) breadcrumbs carry. */
export const scrubBreadcrumbUrls = (breadcrumb: UnknownRecord): UnknownRecord => {
  const data = breadcrumb?.data as UnknownRecord | undefined;
  if (!data) return breadcrumb;
  (['url', 'to', 'from'] as const).forEach((key) => {
    if (typeof data[key] === 'string') {
      data[key] = scrubMigrationParamsFromUrl(data[key]);
    }
  });
  return breadcrumb;
};

/** Mutates and returns the event: request.url, request.headers.Referer, and (defensively) any breadcrumb already attached. */
export const scrubEventUrls = (event: UnknownRecord): UnknownRecord => {
  const request = event.request as UnknownRecord | undefined;
  if (request) {
    if (typeof request.url === 'string') {
      request.url = scrubMigrationParamsFromUrl(request.url);
    }
    const headers = request.headers as UnknownRecord | undefined;
    if (headers && typeof headers.Referer === 'string') {
      headers.Referer = scrubMigrationParamsFromUrl(headers.Referer);
    }
  }
  const breadcrumbs = event.breadcrumbs as UnknownRecord[] | undefined;
  breadcrumbs?.forEach(scrubBreadcrumbUrls);
  return event;
};
